import React from 'react'
import { Button, Card, CardContent, CardHeader, CircularProgress, Typography } from '@material-ui/core'
import { Link } from 'react-router-dom'
import { fetchRecentPortalPayments, sendPortalPaymentReceipt } from '../../api'
import styles from './PortalPaymentsCard.module.css'

const money = (amount, currency = 'USD') => new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency,
}).format(Number(amount || 0))

const paymentDate = (value) => {
  if (!value) return 'Unknown date'
  const date = new Date(value)
  if (Number.isNaN(+date)) return 'Unknown date'
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

const receiptDetails = (payment) => {
  if (payment.receiptStatus === 'sent') {
    return { label: 'Receipt sent', className: styles.receiptSent }
  }
  if (payment.receiptStatus === 'failed') {
    return { label: 'Receipt failed', className: styles.receiptFailed }
  }
  if (payment.receiptStatus === 'sending') {
    return { label: 'Sending receipt', className: styles.receiptSending }
  }
  if (payment.receiptStatus === 'not_sent') {
    return { label: 'Not sent', className: styles.receiptNotSent }
  }
  return { label: 'Receipt pending', className: styles.receiptPending }
}

const PortalPaymentsCard = () => {
  const [payments, setPayments] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState('')
  const [receiptActionError, setReceiptActionError] = React.useState('')
  const [sendingReceiptId, setSendingReceiptId] = React.useState('')

  const loadPayments = React.useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const { data } = await fetchRecentPortalPayments(10)
      setPayments(Array.isArray(data?.payments) ? data.payments : [])
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'Unable to load portal payments.')
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    loadPayments()
  }, [loadPayments])

  const handleSendReceipt = async (payment) => {
    setSendingReceiptId(payment.id)
    setReceiptActionError('')
    setPayments((current) => current.map((entry) => (
      entry.id === payment.id ? { ...entry, receiptStatus: 'sending', receiptError: null } : entry
    )))
    try {
      const { data } = await sendPortalPaymentReceipt(payment.id)
      setPayments((current) => current.map((entry) => (
        entry.id === payment.id ? { ...entry, ...data } : entry
      )))
    } catch (requestError) {
      const message = requestError?.response?.data?.message || 'Unable to send the receipt.'
      setReceiptActionError(message)
      setPayments((current) => current.map((entry) => (
        entry.id === payment.id
          ? { ...entry, receiptStatus: 'failed', receiptError: message }
          : entry
      )))
    } finally {
      setSendingReceiptId('')
    }
  }

  const total = payments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0)

  return (
    <Card variant="outlined" className={styles.card}>
      <CardHeader
        title="Portal Payments"
        subheader={payments.length ? `${payments.length} recent payments • ${money(total)}` : 'ACH payments completed online'}
        action={<Button size="small" onClick={loadPayments} disabled={loading}>Refresh</Button>}
      />
      <CardContent className={styles.cardContent}>
        {loading ? (
          <div className={styles.state}><CircularProgress size={24} /></div>
        ) : error ? (
          <div className={styles.state}>
            <Typography color="error" variant="body2">{error}</Typography>
            <Button size="small" onClick={loadPayments}>Try again</Button>
          </div>
        ) : payments.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            No payments have been completed through the portal yet.
          </Typography>
        ) : (
          <>
          {receiptActionError ? (
            <Typography className={styles.actionError} color="error" variant="body2">
              {receiptActionError}
            </Typography>
          ) : null}
          <div className={styles.table}>
            <div className={styles.header}>
              <span>Customer</span>
              <span>Payment</span>
              <span>Receipt</span>
              <span>Amount</span>
            </div>
            {payments.map((payment) => {
              const receipt = receiptDetails(payment)
              return (
                <div className={styles.row} key={payment.id}>
                  <div className={styles.customer}>
                    <Link to={`/invoice/${payment.invoiceId}`}>
                      {payment.customerName || 'Customer'}
                    </Link>
                    <span>{payment.invoiceNumber ? `Invoice ${payment.invoiceNumber} • ` : ''}{paymentDate(payment.paidAt)}</span>
                  </div>
                  <div className={styles.method}>
                    <strong>{payment.paymentMethod || 'Stripe'}</strong>
                    <span>{payment.stripePaymentIntentId || 'Stripe payment'}</span>
                  </div>
                  <div className={styles.receiptAction}>
                    <span
                      className={`${styles.receiptBadge} ${receipt.className}`}
                      title={payment.receiptError || undefined}
                    >
                      {receipt.label}
                    </span>
                    {!['sent', 'sending'].includes(payment.receiptStatus) ? (
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={sendingReceiptId === payment.id}
                        onClick={() => handleSendReceipt(payment)}
                      >
                        {payment.receiptStatus === 'failed' ? 'Retry' : 'Send receipt'}
                      </Button>
                    ) : null}
                  </div>
                  <strong className={styles.amount}>{money(payment.amount, payment.currency)}</strong>
                </div>
              )
            })}
          </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}

export default PortalPaymentsCard
